/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback, useRef } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'

/**
 * Fields allowed in cards.update() payloads.
 * All other keys are silently dropped to prevent accidental
 * exposure of internal columns (id, created_at, company_id...).
 */
export const CARD_UPDATE_WHITELIST = [
    'title', 'description', 'due_date', 'is_completed',
    'list_id', 'position', 'issue_type', 'priority',
    'estimated_hours', 'logged_hours', 'timer_started_at', 'tracked_seconds', 'parent_id',
    'status', 'recurrence_rule', 'point_value',
    'completion_payload', 'completion_submitted_at', 'completion_submitted_by',
]

/* ========== BOARDS ========== */
export function useBoards(companyId) {
    const [boards, setBoards] = useState([])
    const [loading, setLoading] = useState(true)

    const fetch = useCallback(async () => {
        if (!companyId) { setBoards([]); setLoading(false); return }
        const { data, error } = await kanbify.from('boards').select('*').eq('company_id', companyId).order('position')
        if (error) {
            logDevError('Failed to fetch boards:', error.message)
            setLoading(false)
            return
        }
        setBoards(data || [])
        setLoading(false)
    }, [companyId])

    useEffect(() => { fetch() }, [fetch])

    const add = async (board) => {
        if (!companyId) return null
        const payload = { ...board, company_id: companyId, position: boards.length }
        const { data, error } = await kanbify.from('boards').insert([payload]).select().single()
        if (error) logDevError('[addBoard] RLS/DB error:', error)
        if (data) setBoards(p => [...p, data])
        return data
    }
    const update = async (id, u) => {
        const { error } = await kanbify.from('boards').update(u).eq('id', id)
        if (error) {
            logDevError('[updateBoard] error:', error)
            return { success: false, error: error.message || 'Nu s-a putut actualiza boardul.' }
        }
        setBoards(p => p.map(b => b.id === id ? { ...b, ...u } : b))
        return { success: true }
    }
    const remove = async (id) => {
        try {
            const { error: rpcErr } = await kanbify.rpc('delete_board_cascade', { p_board_id: id })

            if (rpcErr) {
                const { error: directErr } = await kanbify.from('boards').delete().eq('id', id)
                if (directErr) {
                    return {
                        success: false,
                        error: 'Sistemul nu a putut efectua stergerea totala.',
                        details: rpcErr.message || directErr.message,
                    }
                }
            }

            setBoards(p => p.filter(b => b.id !== id))
            return { success: true }
        } catch (err) {
            return {
                success: false,
                error: 'A aparut o eroare neasteptata la stergerea board-ului.',
                details: err.message,
            }
        }
    }
    const reorder = async (newBoards) => {
        setBoards(newBoards)
        const updates = newBoards.map((b, i) => ({ id: b.id, position: i }))
        const { error } = await kanbify.rpc('batch_reorder_boards', { p_updates: updates })
        if (error) {
            for (let i = 0; i < newBoards.length; i++) {
                await kanbify.from('boards').update({ position: i }).eq('id', newBoards[i].id)
            }
        }
    }
    return { boards, loading, fetch, add, update, remove, reorder }
}

/* ========== LISTS ========== */
export function useLists(boardId) {
    const [lists, setLists] = useState([])
    const [loading, setLoading] = useState(true)

    const fetchLists = useCallback(async () => {
        if (!boardId) { setLists([]); setLoading(false); return }
        const { data, error } = await kanbify.from('lists').select('*').eq('board_id', boardId).order('position')
        if (error) {
            logDevError('Failed to fetch lists:', error.message)
            setLoading(false)
            return
        }
        setLists(data || [])
        setLoading(false)
    }, [boardId])

    useEffect(() => { fetchLists() }, [fetchLists])

    useEffect(() => {
        if (!boardId) return
        let timeout
        const channel = kanbify.channel(`lists:${boardId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'lists' }, () => {
                clearTimeout(timeout)
                timeout = setTimeout(() => fetchLists(), 500)
            })
            .subscribe()
        return () => { kanbify.removeChannel(channel); clearTimeout(timeout) }
    }, [boardId, fetchLists])

    const add = async (list) => {
        const { data, error } = await kanbify.from('lists').insert([{ ...list, board_id: boardId, position: lists.length }]).select().single()
        if (error) {
            logDevError('[addList] error:', error)
            return { data: null, error }
        }
        if (data) setLists(p => [...p, data])
        return { data, error: null }
    }
    const update = async (id, u) => {
        const { error } = await kanbify.from('lists').update(u).eq('id', id)
        if (error) {
            logDevError('Failed to update list:', error.message)
            return { success: false, error: error.message }
        }
        setLists(p => p.map(l => l.id === id ? { ...l, ...u } : l))
        return { success: true }
    }
    const remove = async (id) => {
        const { error } = await kanbify.from('lists').delete().eq('id', id)
        if (error) {
            logDevError('[removeList] error:', error)
            return { success: false, error: error.message || 'Nu s-a putut sterge lista.' }
        }
        setLists(p => p.filter(l => l.id !== id))
        return { success: true }
    }
    const reorder = async (newLists) => {
        setLists(newLists)
        const updates = newLists.map((l, i) => ({ id: l.id, position: i }))
        const { error } = await kanbify.rpc('batch_reorder_lists', { p_updates: updates })
        if (error) {
            for (let i = 0; i < newLists.length; i++) {
                await kanbify.from('lists').update({ position: i }).eq('id', newLists[i].id)
            }
        }
    }
    return { lists, loading, fetch: fetchLists, add, update, remove, reorder }
}

/* ========== CARDS ========== */
export function useCards(boardId) {
    const [cards, setCards] = useState([])
    const [loading, setLoading] = useState(true)
    const lastSignatureRef = useRef('')

    const fetchCards = useCallback(async () => {
        if (!boardId) {
            lastSignatureRef.current = ''
            setCards([])
            setLoading(false)
            return
        }
        const listsRes = await kanbify.from('lists').select('id').eq('board_id', boardId)
        if (listsRes.error) {
            logDevError('Failed to fetch lists for cards:', listsRes.error.message)
            setLoading(false)
            return
        }
        const listIds = listsRes.data?.map(l => l.id) || []
        if (!listIds.length) { setCards([]); setLoading(false); return }
        const { data, error } = await kanbify
            .from('cards')
            .select(`
                *,
                card_labels ( label_id, labels:label_id ( id, name, color ) ),
                card_members ( user_id, claimed_at, profiles:user_id ( id, full_name, avatar_url ) ),
                checklists ( id, name, checklist_items ( id, is_completed, is_shared ) ),
                comments ( id ),
                card_completions ( id, user_id, completed_at )
            `)
            .in('list_id', listIds)
            .order('position')
        if (error) {
            logDevError('Failed to fetch cards:', error.message)
            setLoading(false)
            return
        }
        const nextCards = data || []
        const nextSignature = JSON.stringify(nextCards)
        if (nextSignature !== lastSignatureRef.current) {
            lastSignatureRef.current = nextSignature
            setCards(nextCards)
        }
        setLoading(false)
    }, [boardId])

    useEffect(() => { fetchCards() }, [fetchCards])

    useEffect(() => {
        if (!boardId) return
        let timeout
        const debouncedRefetch = () => {
            clearTimeout(timeout)
            timeout = setTimeout(() => fetchCards(), 600)
        }

        // Combine three table subscriptions into a single WebSocket channel.
        // Was 3 separate channels; one channel = one WS subscription.
        const channel = kanbify.channel(`board-cards:${boardId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'cards' }, debouncedRefetch)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'card_members' }, debouncedRefetch)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'card_completions' }, debouncedRefetch)
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
            clearTimeout(timeout)
        }
    }, [boardId, fetchCards])

    const add = async (card) => {
        const existing = cards.filter(c => c.list_id === card.list_id).length
        const { data, error } = await kanbify.from('cards').insert([{ ...card, position: existing }]).select().single()
        if (error) return { data: null, error }
        const full = { ...data, card_labels: [], card_members: [], checklists: [], comments: [], card_completions: [] }
        setCards(p => [...p, full])
        return { data: full, error: null }
    }
    const update = async (id, u) => {
        if (!id) return { success: false, error: 'Card ID required' }
        const payload = {}
        for (const key of CARD_UPDATE_WHITELIST) {
            if (u[key] !== undefined) payload[key] = u[key]
        }

        const { error } = await kanbify.from('cards').update(payload).eq('id', id)
        if (error) return { success: false, error: error.message || 'Update esuat' }

        setCards(p => p.map(c => c.id === id ? { ...c, ...u } : c))
        return { success: true }
    }
    // Soft-delete → archive (Teo 2026-06-02): "deleting" a card sets
    // archived_at; the card disappears from every listing (hidden server-side)
    // but stays restorable from the Arhivă view. Permanent delete happens only
    // when an admin empties the archive.
    const remove = async (id) => {
        const { data, error } = await kanbify.rpc('archive_card', { p_card_id: id })
        if (error || data?.success === false) {
            logDevError('[archiveCard] error:', error || data)
            return { success: false, error: error?.message || data?.error || 'Nu s-a putut arhiva cardul.' }
        }
        setCards(p => p.filter(c => c.id !== id))
        return { success: true }
    }
    const moveAndReorderCards = async (activeCardId, newListId, orderedCards) => {
        setCards(prev => {
            const others = prev.filter(c => c.list_id !== newListId && c.id !== activeCardId)
            const mappedOrdered = orderedCards.map((c, i) => ({ ...c, list_id: newListId, position: i }))
            return [...others, ...mappedOrdered]
        })
        const updates = orderedCards
            .map((c, i) => ({ id: c.id, list_id: newListId, position: i }))
            .filter((_, i) => {
                const card = orderedCards[i]
                return card.id === activeCardId || card.list_id !== newListId || card.position !== i
            })
        if (updates.length > 0) {
            const { error } = await kanbify.rpc('batch_reorder_cards', { p_updates: updates })
            if (error) {
                for (const u of updates) {
                    await kanbify.from('cards').update({ list_id: u.list_id, position: u.position }).eq('id', u.id)
                }
            }
        }
    }
    return { cards, loading, fetch: fetchCards, add, update, remove, moveAndReorderCards }
}

/* ========== LABELS ========== */
export function useLabels(boardId) {
    const [labels, setLabels] = useState([])

    const fetch = useCallback(async () => {
        if (!boardId) return
        const { data, error } = await kanbify.from('labels').select('*').eq('board_id', boardId)
        if (error) {
            logDevError('Failed to fetch labels:', error.message)
            return
        }
        setLabels(data || [])
    }, [boardId])

    useEffect(() => { fetch() }, [fetch])

    const add = async (label) => {
        const { data, error } = await kanbify.from('labels').insert([{ ...label, board_id: boardId }]).select().single()
        if (error) {
            logDevError('Failed to add label:', error.message)
            return { data: null, error: error.message }
        }
        if (data) setLabels(p => [...p, data])
        return { data, error: null }
    }
    const addToCard = async (cardId, labelId) => {
        const { error } = await kanbify.from('card_labels').insert([{ card_id: cardId, label_id: labelId }])
        if (error) logDevError('Failed to add label to card:', error.message)
        return { success: !error, error: error?.message || null }
    }
    const removeFromCard = async (cardId, labelId) => {
        const { error } = await kanbify.from('card_labels').delete().eq('card_id', cardId).eq('label_id', labelId)
        if (error) logDevError('Failed to remove label from card:', error.message)
        return { success: !error, error: error?.message || null }
    }
    return { labels, fetch, add, addToCard, removeFromCard }
}
